import { Loader2, Flame } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { HeatmapMetric } from '@/types/askAi';

const METRICS: { key: HeatmapMetric; label: string; description: string; tone: string }[] = [
  { key: 'fraud_alerts', label: 'Fraud alerts', description: 'Count of open alerts', tone: 'text-red-700' },
  { key: 'fraud_severity', label: 'Fraud severity', description: 'Sum of severity scores', tone: 'text-orange-700' },
  { key: 'suspicion_score', label: 'Suspicion score', description: 'Avg suspicion per cohort', tone: 'text-amber-700' },
  { key: 'transaction_density', label: 'Transaction density', description: 'Tx volume per cell', tone: 'text-blue-700' },
];

interface HeatmapPanelProps {
  activeMetrics: Set<HeatmapMetric>;
  toggleMetric: (m: HeatmapMetric) => void;
  loadingMetric: HeatmapMetric | null;
  gridKm: number;
  setGridKm: (n: number) => void;
  windowDays: number;
  setWindowDays: (n: number) => void;
}

export default function HeatmapPanel({
  activeMetrics,
  toggleMetric,
  loadingMetric,
  gridKm,
  setGridKm,
  windowDays,
  setWindowDays,
}: HeatmapPanelProps) {
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div>
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Flame className="h-4 w-4 text-orange-600" /> Heatmap layers
        </h3>
        <p className="text-xs text-muted-foreground">
          Stack one or more density surfaces over the basemap.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-2 p-3 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-medium">Window</span>
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              className="rounded border bg-background px-1.5 py-0.5 text-xs"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-medium">Grid</span>
            <select
              value={gridKm}
              onChange={(e) => setGridKm(Number(e.target.value))}
              className="rounded border bg-background px-1.5 py-0.5 text-xs"
            >
              <option value={5}>5 km</option>
              <option value={10}>10 km</option>
              <option value={25}>25 km</option>
              <option value={50}>50 km</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {METRICS.map((m) => {
          const active = activeMetrics.has(m.key);
          const busy = loadingMetric === m.key;
          return (
            <Card key={m.key}>
              <CardContent className="flex items-center justify-between gap-2 p-3 text-xs">
                <div className="min-w-0">
                  <div className={`font-semibold ${m.tone}`}>{m.label}</div>
                  <div className="truncate text-muted-foreground">{m.description}</div>
                </div>
                <Button
                  size="sm"
                  variant={active ? 'default' : 'outline'}
                  className="h-7 px-2 text-xs"
                  onClick={() => toggleMetric(m.key)}
                  disabled={busy}
                >
                  {busy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : active ? (
                    'Hide'
                  ) : (
                    'Show'
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
